<template>
  <div
    :data-active="active"
    @dragenter.prevent="setActive(true)"
    @dragover.prevent="setActive(true)"
    @dragleave.prevent="setActive(false)"
    @drop.prevent="onDrop"
    :class="multiple ? 'dropzone-grid' : ''"
    :style="gridStyle">
    <template v-if="!props.showList && files.length">
      <DropZoneImage
        :allowSelection="props.allowSelection"
        :uploadable="file"
        :key="file.id"
        v-for="(file, index) in files"
        :selectable="multiple"
        @toggleSelect="(isSelected) => toggleFileSelect(index, isSelected)"
        @remove="() => removeFile(files[index])" />
    </template>
    <div :class="multiple ? 'dropzone-upload-area' : ''" v-show="props.mediaCount > files.length">
      <label class="dropzone" :for="randomInputId">
        <div class="dz-message text-center">
          <IconFileIsr :size="48" :stroke-width="1" />
          <div class="mt-2">{{ active ? props.dropLabel : props.label }}</div>
        </div>
        <input
          class="d-none"
          type="file"
          :id="randomInputId"
          :multiple="multiple"
          :accept="props.accept"
          @change="onInputChange"
          tabindex="-1"
          ref="fileInput" />
      </label>
    </div>
    <div class="dz-files gap-2" v-if="files.length && props.showList">
      <div class="d-flex justify-content-between align-items-center" v-if="multiple">
        <p class="m-0">{{ uploadedCount }} of {{ files.length }} uploaded</p>
      </div>
      <DropZoneItem :uploadable="file" :key="file.id" v-for="file in files" @remove="removeFile(file)" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { IconFileIsr } from '@tabler/icons-vue';
import type { ImageObject } from '@/types/types';
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import DropZoneItem from './DropZoneItem.vue';
import { type UploadableFile, createId, createFile, startProgress } from './utils';

import { requestMultipart, isAbortError, ClientError } from '@/api/client';
import translator from '@/locale/translator';
import DropZoneImage from './DropZoneImage.vue';
import { useToast } from 'vue-toastification';

const fileInput = ref<HTMLInputElement>();
const randomInputId = Math.random().toString();
const emit = defineEmits([
  'update:modelValue',
  'onFileSelect',
  'fileUploaded',
  'extractionComplete',
  'uploadError',
  'fileRemoved',
]);
const modelValue = defineModel<ImageObject[]>({ required: true });

const props = withDefaults(
  defineProps<{
    label?: string;
    dropLabel?: string;
    mediaCount?: number;
    showList?: boolean;
    allowSelection?: boolean;
    validationFn?: (files: File) => boolean;
    accept?: string;
    formDataName?: string;
    formDataProps?: Record<string, any>;
    uploadLink?: string;
    gridColumns?: number;
    uploadFn?: (file: File, onProgress: (loaded: number, total: number) => void) => Promise<ImageObject>;
  }>(),
  {
    mediaCount: 50,
    showList: false,
    label: translator.get('dropzone.drop_browse'),
    dropLabel: translator.get('dropzone.drop_msg'),
    allowSelection: true,
    uploadLink: `/v1/account/media/images/upload`,
    formDataName: 'image',
    formDataProps: () => ({ class: 0 }),
    gridColumns: 3,
  }
);

defineExpose({ openInput: () => fileInput.value?.click() });

const toast = useToast();
const files = ref<UploadableFile[]>([]);
const uploadedCount = computed(() => files.value.filter((a: UploadableFile) => true === a.status).length || 0);
const multiple = computed(() => props.mediaCount > 1);
const gridStyle = computed(() => ({
  '--grid-columns': props.gridColumns,
}));

function getImageIdentity(image: ImageObject) {
  if (image.id) return `id:${image.id}`;
  return createId(image.description, image.size);
}

function dedupeImages(images: ImageObject[]) {
  const uniqueImages = new Map<string, ImageObject>();

  images.forEach((image) => {
    const identity = getImageIdentity(image);
    if (uniqueImages.has(identity)) uniqueImages.delete(identity);
    uniqueImages.set(identity, image);
  });

  return [...uniqueImages.values()];
}

// When we have initial data - transform it to uploadableFile with 100%
watch(
  modelValue,
  (newVal, oldVal) => {
    if (!files.value.length && !oldVal?.length && newVal.length) {
      dedupeImages(newVal).forEach((v) => {
        files.value.push(createFile(v, true));
      });
    }
  },
  { immediate: true }
);

function toggleFileSelect(index: number, isSelected: boolean) {
  files.value[index].file.selected = isSelected;
}

async function startUpload(file: UploadableFile) {
  if (!file.blob) return;

  const onProgress = (loaded: number, total: number) => {
    file.percentage = Math.round((loaded / total) * 100);
    if (loaded === total && file.phase === 'uploading') {
      file.phase = 'extracting';
      emit('fileUploaded', file);
    }
  };

  try {
    let imageObject: ImageObject;

    if (props.uploadFn) {
      file.phase = 'uploading';
      file.percentage = 0;
      imageObject = await props.uploadFn(file.blob, onProgress);
    } else {
      // Legacy path: fetch-based multipart with fake progress animation
      startProgress(file);
      const formData = new FormData();
      formData.append(props.formDataName, file.blob);
      const res: { data: ImageObject } = await requestMultipart(
        'POST',
        props.uploadLink,
        props.formDataProps,
        formData
      );
      clearInterval(file.interval);
      imageObject = res.data;
    }

    // Preserve the file name, but fill in the rest of the ImageObject
    file.file = { ...imageObject, description: file.file.description, size: file.file.size, selected: true };
    file.percentage = 100;
    file.status = true;
    file.phase = 'done';

    // Update the parent about all uploaded files
    if (multiple.value) modelValue.value = dedupeImages([...modelValue.value, file.file]);
    else modelValue.value = [file.file];
    emit('extractionComplete', file);
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }

    const backendMessage = error instanceof ClientError ? error.responseJson?.message : undefined;
    toast.error(backendMessage || translator.get('data.upload_failed'));

    emit('uploadError', file);
    if (!props.showList) {
      // If we show images, remove the failed file
      return removeFile(file);
    }
    file.status = false;
    file.percentage = 100;
    file.phase = 'failed';
    console.error(error);
  }
}

function addFiles(newFiles: FileList | []) {
  // This case will never happen, but it's good to check
  if (!newFiles.length) return;
  // Extract all non-failed existing file ids
  const existing = files.value.filter((a) => a.status !== false).map((a) => a.id);
  const maxBytes = 10_000_000; // ~10MB

  for (const f of [...newFiles] as File[]) {
    // If the file provided is not an image or is duplicate, skip it
    if (f.size > maxBytes) {
      toast.error(`File ${f.name} is bigger than 10MB`);
      continue;
    }
    if (typeof props.validationFn === 'function') {
      const isValid = props.validationFn(f);
      if (!isValid) {
        toast.error('Invalid file: ' + f.name);
        continue;
      }
    } else if (!f.type.startsWith('image')) {
      toast.error('Invalid file: ' + f.name);
      continue;
    } else if (existing.includes(createId(f.name, f.size))) {
      toast.info('File already added: ' + f.name);
      continue;
    }

    // Create an uploadable object, and mimic full ImageObject
    const uploadable = createFile({ description: f.name, size: f.size, blob: f } as ImageObject);

    let filesLength = 1;
    if (multiple.value) filesLength = files.value.push(uploadable);
    else files.value = [uploadable];

    // Initiate progress bar movement and upload the file
    emit('onFileSelect');
    startUpload(files.value[filesLength - 1]);
    // If NOT multiple, then stop after the first file
    if (!multiple.value || files.value.length >= props.mediaCount) break;
  }
}

function removeFile(file: UploadableFile) {
  const index = files.value.indexOf(file);

  if (index > -1) files.value.splice(index, 1);
  // Update the parent about all remaining files
  modelValue.value = files.value.map((f: UploadableFile) => f.file);
  emit('fileRemoved', file);
}

/* ------------------- Create `active` state and manage it ------------------ */
const active = ref(false);
// The timeout prevents most of the flickering
// see https://www.smashingmagazine.com/2022/03/drag-drop-file-uploader-vuejs-3/#active-state
let inActiveTimeout: ReturnType<typeof setTimeout>;
function setActive(val: boolean) {
  active.value = val;
  if (val) return clearTimeout(inActiveTimeout);
  inActiveTimeout = setTimeout(() => {
    active.value = false;
  }, 30);
}

/* --------------------- FILE INPUT BROWSE OR DRAG-DROP --------------------- */
function onInputChange(e: any) {
  if (!e?.target) return;
  addFiles(e.target.files);
  e.target.value = null;
}
function onDrop(e: DragEvent) {
  setActive(false);
  addFiles(e.dataTransfer?.files || []);
}

/* ------------- PREVENT DEFAULT BROWSER BEHAVIOR ON DRAG-N-DROP ------------ */
const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
function preventDefaults(e: Event) {
  e.preventDefault();
}
onMounted(() => {
  events.forEach((eventName) => {
    document.body.addEventListener(eventName, preventDefaults);
  });
});

onUnmounted(() => {
  events.forEach((eventName) => {
    document.body.removeEventListener(eventName, preventDefaults);
  });
});

// /v1/media/images/upload
</script>
<style scoped>
.dropzone {
  border: 2px dashed var(--tblr-border-color);
  color: var(--tblr-muted);
  padding: 1.4rem;
  min-height: 120px;
  border-radius: var(--tblr-border-radius);
  cursor: pointer;
  display: block;
}

.dropzone .dz-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
  font-family: var(--font-brand);
  color: #8e939d;
}

.dz-files {
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
}

.dropzone-grid {
  display: grid;
  grid-template-columns: repeat(var(--grid-columns), 1fr);
  gap: 10px;
  align-items: start;
}
</style>
